import React, { useState, useEffect, useRef } from 'react'; // Añadido useRef
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Grid, Button, ButtonGroup, Paper } from '@mui/material';
import { getHandicapData, HandicapResponse } from '../../services/hipodromo';
import HandicapIndice from './componenters/handicap_indice';
import HandicapRanking from './componenters/handicap_ranking';

const HandicapView: React.FC<{ raceId?: number }> = ({ raceId }) => {
    const { proCarIdHipodromo, id, hipodromoId } = useParams<{ proCarIdHipodromo?: string, id?: string, hipodromoId?: string }>();
    const [handicapData, setHandicapData] = useState<HandicapResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Estado para controlar qué vista mostrar ('indice' o 'ranking')
    const [viewMode, setViewMode] = useState<'indice' | 'ranking'>('indice');
    // Estado para controlar si la explicación inicial está visible
    const [showExplanation, setShowExplanation] = useState<boolean>(true);

    // Refs para rastrear el estado anterior para el efecto de scroll
    const prevShowExplanation = useRef(showExplanation);
    const prevViewMode = useRef(viewMode);

    // Efecto para buscar datos (sin cambios)
    useEffect(() => {
        const raceIdToUse = raceId || Number(proCarIdHipodromo) || Number(id);
        
        if (!raceIdToUse || isNaN(raceIdToUse)) {
            setError('ID de carrera inválido o no proporcionado.');
            setLoading(false);
            return;
        }

        const fetchHandicap = async () => {
            setLoading(true);
            setError(null);
            // Resetear la vista a la explicación al cambiar de carrera
            setShowExplanation(true);
            setViewMode('indice');
            try {
                const hId = hipodromoId ? Number(hipodromoId) : 1;
                const data = await getHandicapData(raceIdToUse, hId);
                setHandicapData(data);
            } catch (err: any) {
                console.error("Error fetching handicap data:", err);
                setError(err.message || 'Ocurrió un error al cargar los datos del hándicap.');
            } finally {
                setLoading(false);
            }
        };

        fetchHandicap();

    }, [proCarIdHipodromo, id, raceId, hipodromoId]);

    // Efecto para hacer scroll hacia arriba
    useEffect(() => {
        // Comprueba si la explicación acaba de ocultarse
        const explanationJustHidden = !showExplanation && prevShowExplanation.current;
        // Comprueba si el modo de vista cambió MIENTRAS la explicación estaba oculta
        const viewModeChangedWhileHidden = !showExplanation && viewMode !== prevViewMode.current;

        // Realiza el scroll si alguna de las condiciones es verdadera
        if (explanationJustHidden || viewModeChangedWhileHidden) {
             // Usamos requestAnimationFrame para asegurar que el DOM se haya actualizado
             requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
             });
        }

        // Actualiza las refs con los valores actuales para la próxima renderización
        prevShowExplanation.current = showExplanation;
        prevViewMode.current = viewMode;

    }, [showExplanation, viewMode]); // Se ejecuta cuando cambian estos estados

    // --- Renderizado de Loading, Error, No Data (sin cambios) ---
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
                <Typography ml={2}>Cargando datos del Hándicap...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={2}>
                <Alert severity="error" sx={{ width: '100%', maxWidth: '600px' }}>{error}</Alert>
            </Box>
        );
    }

    if (!handicapData) {
        return (
             <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={2}>
                <Alert severity="info" sx={{ width: '100%', maxWidth: '600px' }}>No se encontraron datos para esta carrera.</Alert>
            </Box>
        );
    }

    // --- Renderizado del contenido principal ---
    return (
        <Box sx={{ flexGrow: 1, p: 3, marginBottom: 5 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center" mb={3}>
                Análisis de Hándicap
            </Typography>

            {/* Botones siempre visibles debajo del título */}
            <Box display="flex" justifyContent="center" mb={3}>
                <ButtonGroup variant="outlined" aria-label="selector de vista hándicap">
                    <Button
                        onClick={() => {
                            setShowExplanation(false); // Oculta la explicación
                            setViewMode('indice');     // Establece la vista a 'indice'
                        }}
                        // Resaltado si la explicación está oculta y el modo es 'indice'
                        variant={!showExplanation && viewMode === 'indice' ? 'contained' : 'outlined'}
                    >
                        Ver Índice
                    </Button>
                    <Button
                        onClick={() => {
                            setShowExplanation(false); // Oculta la explicación
                            setViewMode('ranking');    // Establece la vista a 'ranking'
                        }}
                        // Resaltado si la explicación está oculta y el modo es 'ranking'
                        variant={!showExplanation && viewMode === 'ranking' ? 'contained' : 'outlined'}
                    >
                        Ver Ranking
                    </Button>
                </ButtonGroup>
            </Box>

            {/* Área de Contenido Dinámico */}
            <Grid container justifyContent="center">
                 {/* Ajusta el tamaño según lo que se muestra */}
                <Grid item xs={12} md={10} lg={showExplanation ? 12 : (viewMode === 'indice' ? 8 : 6)}>
                    {showExplanation ? (
                        // Mostrar la explicación si showExplanation es true
                        <Paper elevation={2} sx={{ p: 2 }}>
                             <Typography variant="h6" gutterBottom>
                                ¿Qué es el Hándicap y el Ranking?
                            </Typography>
                            <Typography variant="body1" paragraph>
                                El <strong>Hándicap</strong> en las carreras de caballos busca igualar la competencia asignando peso adicional a los caballos según su rendimiento pasado y habilidad estimada. Los mejores caballos cargan más peso, dando una oportunidad teórica a los menos favorecidos.
                            </Typography>
                             <Typography variant="body1" paragraph>
                                El cálculo se basa en analizar factores como tiempos, posiciones, calidad de rivales, distancia y estado de la pista para asignar una valoración (rating) que determina el peso a llevar. No hay una única fórmula universal, a menudo interviene el juicio de un experto ("handicapper").
                             </Typography>
                            <Typography variant="body1" paragraph>
                               El <strong>Ranking</strong> clasifica a los caballos según esta valoración o análisis de hándicap para una carrera específica. Un ranking más alto sugiere una mayor probabilidad teórica de buen desempeño. Sirve como guía para evaluar las oportunidades relativas de cada participante.
                            </Typography>
                        </Paper>
                    ) : (
                        // Mostrar el componente correspondiente si la explicación está oculta
                        <>
                            {viewMode === 'indice' && handicapData.participantes_resumen && (
                                <HandicapIndice participantes={handicapData.participantes_resumen} />
                            )}
                            {viewMode === 'ranking' && handicapData.ranking_general && (
                                <HandicapRanking ranking={handicapData.ranking_general} />
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default HandicapView;